"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createIntersectionObserver = exports.isVisible = void 0;
const throttle_1 = __importDefault(require("lodash/throttle"));
const react_1 = require("react");
const libs_hooks_react_1 = require("@hotelplan/libs.hooks-react");
const libs_polyfills_1 = require("@hotelplan/libs.polyfills");
const IS_OBSERVER_SUPPORTED = typeof IntersectionObserver !== 'undefined';
function normalizeRect(rect) {
    return {
        top: rect.top,
        left: rect.left,
        bottom: rect.bottom,
        right: rect.right,
        width: rect.right - rect.left,
        height: rect.bottom - rect.top,
    };
}
function roundRectDown(rect) {
    return {
        top: Math.floor(rect.top),
        left: Math.floor(rect.left),
        bottom: Math.floor(rect.bottom),
        right: Math.floor(rect.right),
    };
}
function isVisible(rootRect, rect, partial) {
    return partial
        ? rect.top <= rootRect.bottom &&
            rect.bottom >= rootRect.top &&
            rect.left <= rootRect.right &&
            rect.right >= rootRect.left
        : rect.top >= rootRect.top &&
            rect.left >= rootRect.left &&
            rect.bottom <= rootRect.bottom &&
            rect.right <= rootRect.right;
}
exports.isVisible = isVisible;
function createIntersectionObserver(el, callback, options) {
    const { root, partial = true, margin } = options;
    if (IS_OBSERVER_SUPPORTED) {
        const observer = new IntersectionObserver(([entry]) => callback(entry.isIntersecting), {
            root,
            rootMargin: typeof margin === 'number' ? `${margin}px` : undefined,
        });
        observer.observe(el);
        return () => {
            observer.disconnect();
        };
    }
    let rootRect;
    if (root && root !== window) {
        const rootDOMRect = root.getBoundingClientRect();
        rootRect = {
            top: rootDOMRect.top,
            left: rootDOMRect.left,
            bottom: rootDOMRect.bottom,
            right: rootDOMRect.right,
        };
    }
    else {
        rootRect = {
            top: 0,
            left: 0,
            bottom: window.innerHeight || document.documentElement.clientHeight,
            right: window.innerWidth || document.documentElement.clientWidth,
        };
    }
    if (margin) {
        rootRect.top -= margin;
        rootRect.left -= margin;
        rootRect.bottom += margin;
        rootRect.right += margin;
    }
    function checkIntersecting() {
        const rect = normalizeRect(roundRectDown(el.getBoundingClientRect()));
        callback(isVisible(rootRect, rect, partial));
    }
    window.addEventListener('scroll', checkIntersecting, libs_polyfills_1.optionsWithPassive);
    window.addEventListener('resize', checkIntersecting, libs_polyfills_1.optionsWithPassive);
    return () => {
        window.removeEventListener('scroll', checkIntersecting);
        window.removeEventListener('resize', checkIntersecting);
    };
}
exports.createIntersectionObserver = createIntersectionObserver;
function useOnScreen(ref, options = {}) {
    const { throttle: throttleTime = 200, margin, root, partial = true, onChange, disable = false, } = options;
    const onChangeRef = (0, libs_hooks_react_1.useSelfUpdatingRef)(onChange);
    const [isOnScreen, setOnScreen] = (0, react_1.useState)(false);
    (0, react_1.useEffect)(function observerEffect() {
        if (disable)
            return;
        const el = ref.current;
        if (!el)
            return;
        function setter(next) {
            const onChangeHandler = onChangeRef.current;
            setOnScreen(onChangeHandler
                ? prev => {
                    if (prev !== next)
                        onChangeHandler(next);
                    return next;
                }
                : next);
        }
        const intersectionCallback = (0, throttle_1.default)(setter, throttleTime);
        return createIntersectionObserver(el, intersectionCallback, {
            root,
            partial,
            margin,
        });
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disable, ref]);
    return { isOnScreen };
}
exports.default = useOnScreen;
//# sourceMappingURL=useOnScreen.js.map